/**
 * Platform-specific bundled content loaders
 * React Native and React Web implementations
 */

import { ContentBundle, ContentBundleLoader, BundledContentRecord } from './contentBundler';

// Type declarations for browser APIs
declare global {
  interface Navigator {
    product?: string;
  }
  
  // Window and IndexedDB declarations for environments without DOM types
  interface Window {
    indexedDB?: IDBFactory;
  }
}

// IndexedDB type declarations for better compatibility
interface IDBFactory {
  open(name: string, version?: number): IDBOpenDBRequest;
}

interface IDBOpenDBRequest extends IDBRequest {
  result: IDBDatabase;
  onupgradeneeded: ((event: IDBVersionChangeEvent) => void) | null;
}

interface IDBRequest {
  result: any;
  error: DOMException | null;
  onsuccess: ((event: Event) => void) | null;
  onerror: ((event: Event) => void) | null;
}

interface IDBDatabase {
  transaction(storeNames: string | string[], mode?: IDBTransactionMode): IDBTransaction;
  objectStoreNames: DOMStringList;
  createObjectStore(name: string, options?: IDBObjectStoreParameters): IDBObjectStore;
}

interface IDBTransaction {
  objectStore(name: string): IDBObjectStore;
}

interface IDBObjectStore {
  getAll(): IDBRequest;
}

interface IDBVersionChangeEvent {
  target: IDBOpenDBRequest & EventTarget;
}

interface IDBObjectStoreParameters {
  keyPath?: string;
}

interface DOMStringList {
  contains(string: string): boolean;
}

type IDBTransactionMode = 'readonly' | 'readwrite';

interface Storage {
  getItem(key: string): string | null;
}

// IndexedDB types for better type safety
interface BundledRecord {
  id: string;
  contentType: string;
  languageId: string;
  payload: any;
  version: string;
  bundledAt: string;
}

/**
 * React Native Implementation
 * Loads content from bundled assets (JSON files, SQLite database)
 */
export class ReactNativeBundleLoader extends ContentBundleLoader {
  /**
   * Load content bundle from React Native assets
   */
  async loadFromAssets(): Promise<void> {
    try {
      console.log('📱 ReactNative: Loading bundled content from assets...');
      
      // Try static require paths (Metro requires static paths at build time)
      let bundledContentJson;
      
      // Try path 1: From shared to mobile assets
      try {
        console.log('📱 Trying asset path: ../../../../mobile/assets/content/bundled-content.json');
        bundledContentJson = require('../../../../mobile/assets/content/bundled-content.json');
        console.log('📱 Successfully loaded from: ../../../../mobile/assets/content/bundled-content.json');
      } catch (error1) {
        console.log('📱 Failed path 1:', error1 instanceof Error ? error1.message : String(error1));
        
        // Try path 2: Alternative mobile assets path
        try {
          console.log('📱 Trying asset path: ../../../mobile/assets/content/bundled-content.json');
          bundledContentJson = require('../../../mobile/assets/content/bundled-content.json');
          console.log('📱 Successfully loaded from: ../../../mobile/assets/content/bundled-content.json');
        } catch (error2) {
          console.log('📱 Failed path 2:', error2 instanceof Error ? error2.message : String(error2));
          
          // Try path 3: Another alternative
          try {
            console.log('📱 Trying asset path: ../../mobile/assets/content/bundled-content.json');
            bundledContentJson = require('../../mobile/assets/content/bundled-content.json');
            console.log('📱 Successfully loaded from: ../../mobile/assets/content/bundled-content.json');
          } catch (error3) {
            console.log('📱 Failed path 3:', error3 instanceof Error ? error3.message : String(error3));
            throw new Error('Bundled content not found in any asset path');
          }
        }
      }
      
      if (!bundledContentJson) {
        throw new Error('Bundled content not found in any asset path');
      }
      
      const bundle: ContentBundle = bundledContentJson;
      
      await this.loadBundledContent(bundle);
      await this.markBundledContentLoaded(bundle.version);
      
      console.log('📱 ReactNative: Bundled content loaded successfully');
    } catch (error) {
      console.error('📱 ReactNative: Failed to load bundled content from assets:', error);
      
      // Fallback: Try loading from storage if assets fail
      await this.loadFromStorage();
    }
  }

  /**
   * Alternative: Load from pre-populated SQLite database
   */
  async loadFromSQLite(): Promise<void> {
    try {
      console.log('📱 ReactNative: Loading bundled content from SQLite...');
      
      // This would use react-native-sqlite-storage or similar
      // Implementation depends on your SQLite setup
      
      const sqlite = require(/* @vite-ignore */ 'react-native-sqlite-storage');
      const db = sqlite.openDatabase(
        { name: 'bundled-content.db', location: 'default' },
        () => console.log('📱 SQLite database opened'),
        (error: any) => console.error('📱 SQLite error:', error)
      );

      // Query bundled content from SQLite
      return new Promise((resolve, reject) => {
        db.transaction((tx: any) => {
          tx.executeSql(
            'SELECT * FROM bundled_content ORDER BY id',
            [],
            async (tx: any, results: any) => {
              const records = [];
              for (let i = 0; i < results.rows.length; i++) {
                const row = results.rows.item(i);
                records.push({
                  id: row.content_id,
                  contentType: row.content_type,
                  languageId: row.language_id,
                  payload: JSON.parse(row.payload),
                  version: row.version,
                  bundledAt: row.created_at
                });
              }
              
              const bundle: ContentBundle = {
                version: '1.0.0',
                generatedAt: new Date().toISOString(),
                records,
                metadata: {
                  totalRecords: records.length,
                  contentTypes: [...new Set(records.map(r => r.contentType))],
                  languages: [...new Set(records.map(r => r.languageId))],
                  description: 'SQLite bundled content'
                }
              };
              
              await this.loadBundledContent(bundle);
              await this.markBundledContentLoaded(bundle.version);
              resolve(undefined);
            },
            (tx: any, error: any) => {
              console.error('📱 SQLite query error:', error);
              reject(error);
            }
          );
        });
      });
    } catch (error) {
      console.error('📱 ReactNative: Failed to load from SQLite:', error);
      throw error;
    }
  }

  /**
   * Load from storage as fallback
   */
  private async loadFromStorage(): Promise<void> {
    try {
      console.log('📱 ReactNative: Loading bundled content from storage (fallback)...');
      
      if (!this.storage) {
        throw new Error('Storage not available');
      }

      const bundledContentString = await this.storage.getItem('content_bundle');
      if (!bundledContentString) {
        throw new Error('No bundled content found in storage');
      }

      const bundle: ContentBundle = JSON.parse(bundledContentString);
      await this.loadBundledContent(bundle);
      await this.markBundledContentLoaded(bundle.version);
      
      console.log('📱 ReactNative: Loaded bundled content from storage');
    } catch (error) {
      console.error('📱 ReactNative: Failed to load from storage:', error);
      throw error;
    }
  }
}

/**
 * React Web Implementation
 * Loads content from bundled public assets or CDN
 */
export class ReactWebBundleLoader extends ContentBundleLoader {
  /**
   * Load content bundle from web assets (public folder)
   */
  async loadFromPublicAssets(): Promise<void> {
    try {
      console.log('🌐 ReactWeb: Loading bundled content from public assets...');

      // Load from public/assets/content/bundled-content.json
      const response = await fetch('/assets/content/bundled-content.json');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch bundled content: ${response.status}`);
      }
      
      const bundle: ContentBundle = await response.json();
      
      await this.loadBundledContent(bundle);
      await this.markBundledContentLoaded(bundle.version);
      
      console.log('🌐 ReactWeb: Bundled content loaded successfully');
    } catch (error) {
      console.error('🌐 ReactWeb: Failed to load bundled content from public assets:', error);
      
      // Fallback: Try loading from localStorage
      await this.loadFromLocalStorage();
    }
  }

  /**
   * Load from IndexedDB for larger datasets
   */
  async loadFromIndexedDB(): Promise<void> {
    try {
      console.log('🌐 ReactWeb: Loading bundled content from IndexedDB...');
      
      // Check if we're in a browser environment and IndexedDB is available
      const globalWindow = typeof window !== 'undefined' ? window : undefined;
      if (!globalWindow || !globalWindow.indexedDB) {
        throw new Error('IndexedDB not available');
      }
      
      return new Promise<void>((resolve, reject) => {
        const request = globalWindow.indexedDB!.open('BundledContentDB', 1);
        
        request.onerror = () => {
          reject(new Error('Failed to open IndexedDB'));
        };

        request.onsuccess = async (event: Event) => {
          const db = (event.target as unknown as IDBOpenDBRequest).result;
          const transaction = db.transaction(['content'], 'readonly');
          const store = transaction.objectStore('content');
          const getAllRequest = store.getAll();

          getAllRequest.onsuccess = async () => {
            const records: BundledRecord[] = getAllRequest.result;
            
            const bundle: ContentBundle = {
              version: '1.0.0',
              generatedAt: new Date().toISOString(),
              records: records.map(r => ({
                id: r.id,
                contentType: r.contentType,
                languageId: r.languageId,
                payload: r.payload,
                version: r.version,
                bundledAt: r.bundledAt
              })),
              metadata: {
                totalRecords: records.length,
                contentTypes: [...new Set(records.map((r: BundledRecord) => r.contentType))],
                languages: [...new Set(records.map((r: BundledRecord) => r.languageId))],
                description: 'IndexedDB bundled content'
              }
            };
            
            await this.loadBundledContent(bundle);
            await this.markBundledContentLoaded(bundle.version);
            resolve();
          };

          getAllRequest.onerror = () => {
            reject(new Error('Failed to read from IndexedDB'));
          };
        };

        request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains('content')) {
            db.createObjectStore('content', { keyPath: 'id' });
          }
        };
      });
    } catch (error) {
      console.error('🌐 ReactWeb: Failed to load from IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Load from localStorage as fallback
   */
  private async loadFromLocalStorage(): Promise<void> {
    try {
      console.log('🌐 ReactWeb: Loading bundled content from localStorage (fallback)...');
      
      // Check if localStorage is available
      const globalStorage = typeof localStorage !== 'undefined' ? localStorage : undefined;
      if (!globalStorage) {
        throw new Error('localStorage not available');
      }

      const bundledContentString = globalStorage.getItem('content_bundle');
      if (!bundledContentString) {
        throw new Error('No bundled content found in localStorage');
      }

      const bundle: ContentBundle = JSON.parse(bundledContentString);
      await this.loadBundledContent(bundle);
      await this.markBundledContentLoaded(bundle.version);
      
      console.log('🌐 ReactWeb: Loaded bundled content from localStorage');
    } catch (error) {
      console.error('🌐 ReactWeb: Failed to load from localStorage:', error);
      throw error;
    }
  }
}

/**
 * Platform-agnostic initialization
 */
export class BundledContentInitializer {
  constructor(
    private storage: any,
    private useContentStore: any
  ) {}

  /**
   * Initialize bundled content based on platform
   */
  async initialize(): Promise<void> {
    try {
      console.log('🚀 Initializing bundled content...');
      
      // Determine platform - React Native detection
      const isReactNative = typeof navigator !== 'undefined' && 
                            navigator.product === 'ReactNative';
      
      let loader: ContentBundleLoader;
      
      if (isReactNative) {
        console.log('📱 Detected React Native platform');
        loader = new ReactNativeBundleLoader(this.storage, this.useContentStore);
        
        // Try loading from assets first, then SQLite
        try {
          await (loader as ReactNativeBundleLoader).loadFromAssets();
        } catch (assetsError) {
          console.warn('📱 Assets loading failed, trying SQLite...');
          await (loader as ReactNativeBundleLoader).loadFromSQLite();
        }
      } else {
        console.log('🌐 Detected React Web platform');
        loader = new ReactWebBundleLoader(this.storage, this.useContentStore);
        
        // Try loading from public assets first, then IndexedDB
        try {
          await (loader as ReactWebBundleLoader).loadFromPublicAssets();
        } catch (assetsError) {
          console.warn('🌐 Public assets loading failed, trying IndexedDB...');
          await (loader as ReactWebBundleLoader).loadFromIndexedDB();
        }
      }
      
      console.log('🚀 Bundled content initialization complete');
    } catch (error) {
      console.error('🚀 Bundled content initialization failed:', error);
      // Don't throw - app should still work without bundled content
    }
  }

  /**
   * Check if initialization is needed
   */
  async shouldInitialize(): Promise<boolean> {
    const loader = new ContentBundleLoader(this.storage, this.useContentStore);
    return loader.shouldLoadBundledContent();
  }
}